import { useSwapContext } from "@src/contexts";
import { PageLayout, Navigation } from "@src/components";
import { shift4Config } from "@src/config";
import { ErrorView } from "../ErrorView";
import { useShift4SDK } from "@src/hooks/useShift4SDK";
import { useMemo } from "react";

const getDirectOnRampFormConfiguration = ({
  baseChain,
  externalWalletAddress,
  themeMode = "light",
  themeColor = "#836FFF",
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formConfiguration: any = {
    apiKey: baseChain.shift4Pk,
    cryptoCurrencyCode: shift4Config[baseChain.chainId]!.USDC,
    fiatCurrencyCode: "USD",
    lockedAmount: false,
    walletAddress: externalWalletAddress,
    showWalletAddressForm: false,
    themeMode,
    widgetWidth: 320,
    themeRadius: 20,
    themeColor,
    paymentMethod: "card",
  };

  return formConfiguration;
};

export const DirectOnRampView = () => {
  const { integrationConfig, supportedChains, isFetchingChains } =
    useSwapContext();

  // Find the destination chain from integration config
  const destinationChain = supportedChains.find(
    (chain) => chain.chainId === integrationConfig.dstChainId,
  );

  // Create form configuration
  const formConfiguration = useMemo(
    () =>
      destinationChain && integrationConfig.externalWalletAddress
        ? getDirectOnRampFormConfiguration({
            baseChain: destinationChain,
            externalWalletAddress: integrationConfig.externalWalletAddress,
            themeMode: integrationConfig.styles?.themeMode,
            themeColor: integrationConfig.styles?.brandTheme?.primaryColor,
          })
        : null,
    [
      destinationChain,
      integrationConfig.externalWalletAddress,
      integrationConfig.styles?.themeMode,
      integrationConfig.styles?.brandTheme?.primaryColor,
    ],
  );

  // Use the hook
  const { containerRef, notLoadedInTime } = useShift4SDK({
    formConfiguration,
    dependencies: [
      destinationChain,
      integrationConfig.externalWalletAddress,
      integrationConfig.styles,
      isFetchingChains,
      supportedChains,
    ],
  });

  if (notLoadedInTime) {
    return (
      <ErrorView
        title="Deposit failed"
        description="Loading Shift4 widget failed"
      />
    );
  }

  // Show loading state while fetching chains
  if (isFetchingChains) {
    return (
      <PageLayout
        navigation={
          <Navigation
            leftItems={[
              {
                content: "Direct Deposit",
                disabled: true,
              },
            ]}
          />
        }
      >
        <div className="flex justify-center items-center h-full">
          <div className="text-center">
            <div className="text-lg font-medium text-text_primary">
              Loading...
            </div>
            <div className="text-sm text-text_primary/70 mt-2">
              Fetching supported chains
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Show error if destination chain is not supported
  if (supportedChains.length > 0 && !destinationChain) {
    return (
      <ErrorView
        title="Chain Not Supported"
        description={`Chain ${integrationConfig.dstChainId} is not supported by this integrator.`}
      />
    );
  }

  return (
    <PageLayout
      navigation={
        <Navigation
          leftItems={[
            {
              content: "Direct Deposit",
              disabled: true,
            },
          ]}
        />
      }
    >
      <div
        className={`flex justify-center rounded-[12px] overflow-hidden 
        ${
          integrationConfig.styles?.themeMode === "dark"
            ? "bg-black"
            : "bg-white"
        }`}
      >
        <div ref={containerRef} style={{ width: "320px", height: "480px" }} />
      </div>
    </PageLayout>
  );
};
